(function () {
  'use strict';

  if (window.ElBorceguiHome) { window.ElBorceguiHome.init(document); return; }
  const instances = new WeakMap();

  function shuffled(items) {
    const result = items.slice();
    for (let i = result.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [result[i], result[j]] = [result[j], result[i]];
    }
    return result;
  }

  function modelKey(card) {
    return card.dataset.productModel || card.dataset.productHandle;
  }

  function readPrevious(key) {
    try {
      const value = JSON.parse(sessionStorage.getItem(key) || '[]');
      return Array.isArray(value) ? value.filter(item => typeof item === 'string') : [];
    } catch (_) { return []; }
  }

  function rotateProducts(section, usedModels) {
    const grid = section.querySelector('[data-product-grid]');
    if (!grid) return;
    const pools = {};
    section.querySelectorAll('template[data-pool]').forEach(template => {
      pools[template.dataset.pool] = Array.from(template.content.querySelectorAll('[data-product-handle]'));
    });
    const key = 'eb-home-selection-v1:' + section.dataset.rotation;
    const previous = new Set(readPrevious(key));
    const selected = [];
    const localModels = new Set(usedModels);
    function take(cards, count) {
      const candidates = shuffled(cards || []);
      // Prefer products not shown on the previous load; a short pool still works.
      candidates.sort((a, b) => Number(previous.has(modelKey(a))) - Number(previous.has(modelKey(b))));
      let added = 0;
      for (const card of candidates) {
        const model = modelKey(card);
        if (!model || localModels.has(model)) continue;
        selected.push(card);
        localModels.add(model);
        added += 1;
        if (added === count) break;
      }
    }
    if (section.dataset.rotation === 'favorites') {
      take(pools.new, 2);
      take(pools.proven, 2);
      if (selected.length < 4) take([...(pools.new || []), ...(pools.proven || [])], 4 - selected.length);
    } else {
      take(pools.sale, 4);
    }
    if (!selected.length) return; // Keep the server-rendered fallback.
    const fragment = document.createDocumentFragment();
    selected.forEach(card => {
      fragment.appendChild(card.cloneNode(true));
      usedModels.add(modelKey(card));
    });
    grid.replaceChildren(fragment);
    try { sessionStorage.setItem(key, JSON.stringify(selected.map(modelKey))); } catch (_) { /* Storage is optional. */ }
    section.dataset.rotationReady = 'true';
  }

  function initCarousel(carousel) {
    const slides = Array.from(carousel.querySelectorAll('[data-slide]'));
    const controls = carousel.querySelector('[data-carousel-controls]');
    if (!slides.length) return { destroy() {}, select() {}, deselect() {} };
    const previous = controls?.querySelector('[data-prev]');
    const next = controls?.querySelector('[data-next]');
    const status = controls?.querySelector('[data-slide-status]');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const events = new AbortController();
    const on = (target, type, handler) => target?.addEventListener(type, handler, {signal: events.signal});
    const requestedInterval = Number(carousel.dataset.interval);
    const interval = Number.isFinite(requestedInterval) && requestedInterval >= 5000 && requestedInterval <= 12000 ? requestedInterval : 6500;
    let index = 0;
    let motionReduced = reducedMotion.matches;
    let hovered = false;
    let focused = false;
    let visible = true;
    let editing = carousel.dataset.designMode === 'true';
    let timer = null;

    function updateStatusMode() {
      if (status) status.setAttribute('aria-live', motionReduced ? 'polite' : 'off');
    }
    function schedule() {
      window.clearTimeout(timer);
      timer = null;
      if (slides.length < 2 || motionReduced || hovered || focused || editing || !visible || document.hidden || !carousel.isConnected) return;
      timer = window.setTimeout(() => { show(index + 1); schedule(); }, interval);
    }
    function show(target) {
      index = (target + slides.length) % slides.length;
      slides.forEach((slide, position) => {
        slide.hidden = position !== index;
        slide.classList.toggle('is-entering', position === index);
      });
      if (status) status.textContent = (index + 1) + ' / ' + slides.length;
    }
    function manualStep(direction) {
      // Arrows restart a full interval; reduced motion still disables autoplay.
      hovered = false;
      focused = false;
      show(index + direction);
      schedule();
    }
    on(previous, 'click', () => manualStep(-1));
    on(next, 'click', () => manualStep(1));
    // Keep clickable slide content stable, but never suspend on arrow controls.
    on(carousel, 'mouseover', event => {
      const overSlide = Boolean(event.target.closest('[data-slide]'));
      if (hovered !== overSlide) { hovered = overSlide; schedule(); }
    });
    on(carousel, 'mouseleave', () => { hovered = false; schedule(); });
    const isSlideContent = element => Boolean(element && carousel.contains(element) && element.closest('[data-slide]'));
    on(carousel, 'focusin', event => { focused = isSlideContent(event.target); schedule(); });
    on(carousel, 'focusout', event => { focused = isSlideContent(event.relatedTarget); schedule(); });
    on(controls, 'keydown', event => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      manualStep(event.key === 'ArrowRight' ? 1 : -1);
    });
    on(document, 'visibilitychange', schedule);
    on(reducedMotion, 'change', event => {
      motionReduced = event.matches;
      updateStatusMode(); schedule();
    });
    let observer;
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(entries => { visible = Boolean(entries[0]?.isIntersecting); schedule(); }, {threshold: 0.15});
      observer.observe(carousel);
    }
    if (controls) controls.hidden = slides.length < 2;
    updateStatusMode();
    show(0);
    schedule();
    return {
      destroy() { window.clearTimeout(timer); observer?.disconnect(); events.abort(); },
      select(blockId) {
        const selected = slides.findIndex(slide => slide.dataset.blockId === blockId);
        if (selected < 0) return;
        editing = true; show(selected); schedule();
      },
      deselect() { editing = carousel.dataset.designMode === 'true'; schedule(); }
    };
  }

  function initAnchors(home) {
    const events = new AbortController();
    home.addEventListener('click', event => {
      const link = event.target.closest('a[href^="#eb-home-"]');
      if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const target = document.getElementById(link.getAttribute('href').slice(1));
      if (!target || !home.contains(target)) return;
      event.preventDefault();
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      // Preserve keyboard navigation after moving away from the hero.
      target.setAttribute('tabindex', '-1');
      target.focus({preventScroll: true});
      target.scrollIntoView({behavior: reduced ? 'instant' : 'smooth', block: 'start'});
      if (window.location.hash !== link.hash) {
        try { window.history.pushState(null, '', link.hash); } catch (_) { /* Scrolling still works. */ }
      }
    }, {signal: events.signal});
    return () => events.abort();
  }

  function homesWithin(root) {
    const selector = '.eb-home[data-home-refinement]';
    return [...(root.matches?.(selector) ? [root] : []), ...root.querySelectorAll(selector)];
  }

  function init(root) {
    homesWithin(root).forEach(home => {
      if (instances.has(home)) return;
      home.dataset.initialized = 'true';
      const usedModels = new Set();
      home.querySelectorAll('[data-rotation]').forEach(section => rotateProducts(section, usedModels));
      const carousel = home.querySelector('[data-carousel]');
      const controller = carousel ? initCarousel(carousel) : null;
      const removeAnchors = initAnchors(home);
      instances.set(home, {controller, destroy() { controller?.destroy(); removeAnchors(); }});
    });
  }
  window.ElBorceguiHome = {init};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), {once: true});
  else init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => homesWithin(event.target).forEach(home => {
    instances.get(home)?.destroy(); instances.delete(home); delete home.dataset.initialized;
  }));
  document.addEventListener('shopify:block:select', event => {
    const home = event.target.closest('.eb-home') || event.target.querySelector('.eb-home');
    instances.get(home)?.controller?.select(event.detail?.blockId);
  });
  document.addEventListener('shopify:block:deselect', event => {
    const home = event.target.closest('.eb-home') || event.target.querySelector('.eb-home');
    instances.get(home)?.controller?.deselect();
  });
})();
